"use client";

import { ErrorMessage } from "@hookform/error-message";
import { type ComponentProps, type ReactNode, forwardRef, useId } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { FormErrorMessage, cn } from "../../..";

interface CheckboxProps extends ComponentProps<"input"> {
	label?: string | ReactNode;
	registerOptions?: RegisterOptions<FieldValues>;
	showErrors?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
	function CheckboxRef(props, ref) {
		const { label, className, registerOptions, children, showErrors = true, ...rest } = props;
		const methods = useFormContext();

		const registeredProps =
			methods && props.name ? methods.register(props.name, registerOptions) : { ref };

		const id = useId();
		const hasError = props.name ? methods?.getFieldState(props.name)?.invalid : false;

		return (
			<>
				<div className={cn("flex items-center", className)}>
					<input
						id={id}
						type="checkbox"
						{...rest}
						{...registeredProps}
						className={cn(
							"disabled:cursor-not-allowed disabled:opacity-70",
							"form-checkbox cursor-pointer rounded border-shades text-primary-medium hover:border-primary-medium focus:outline-none focus:ring-primary-medium focus-visible:border-primary-medium focus-visible:ring-primary-medium motion-safe:transition-colors",
						)}
					/>

					<label
						htmlFor={id}
						className={cn("ml-2 leading-tight", { "opacity-70": props.disabled })}
					>
						{label || children}
					</label>
				</div>

				{showErrors && hasError && !!props.name && (
					<FormErrorMessage variant="danger">
						<ErrorMessage name={props.name} />
					</FormErrorMessage>
				)}
			</>
		);
	},
);
